import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Video, Microscope, PenTool, Lightbulb, BookOpen, Check, ArrowRight, Circle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useEffect, useMemo, useRef, useState } from 'react';

const iconMap = {
  Watch: BookOpen, // Use BookOpen icon for Learn & Discover
  Video: Video, // Use Video icon for Video type
  Explore: Microscope,
  Practice: PenTool,
  Curiosity: Lightbulb
};

const typeColors = {
  Watch: 'from-blue-500 to-purple-600',
  Video: 'from-red-500 via-pink-500 to-purple-500', // Video type has red-pink-purple gradient
  Explore: 'from-green-500 to-teal-600',
  Practice: 'from-orange-500 to-red-600',
  Curiosity: 'from-yellow-500 to-amber-600'
};

export default function ActivityProgressList({
  items,
  currentIndex,
  completedItems,
  onSelectIndex = null,
  multipleSectionKeys = null, // Array of section keys to expand (for merged sections)
}) {
  // State must be declared BEFORE all useEffects
  const prevGroupKeySignatureRef = useRef(null);
  const [openSections, setOpenSections] = useState([]);

  // Debug: Log when currentIndex changes
  useEffect(() => {
    console.log(`📊 [ActivityProgressList] currentIndex changed to: ${currentIndex}`);
  }, [currentIndex]);

  // Handle merged sections: expand multiple sections when provided
  useEffect(() => {
    console.log(`🔍 [Accordion] multipleSectionKeys changed:`, multipleSectionKeys);
    if (multipleSectionKeys && Array.isArray(multipleSectionKeys) && multipleSectionKeys.length > 0) {
      console.log(`📂 [Accordion] Expanding MULTIPLE sections (${multipleSectionKeys.length}):`, multipleSectionKeys);
      setOpenSections(multipleSectionKeys);
      console.log(`✅ [Accordion] setOpenSections called with:`, multipleSectionKeys);
    } else {
      console.log(`ℹ️ [Accordion] No merged sections or empty array`);
    }
  }, [multipleSectionKeys]);

  // Debug: Log openSections state changes
  useEffect(() => {
    console.log(`📋 [Accordion] openSections state is now:`, openSections);
  }, [openSections]);




  const groupedItems = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const groups = [];
    const indexByKey = new Map();

    const getGroupKey = (item) => {
      const name = typeof item?.section_name === 'string' ? item.section_name.trim() : '';
      const number = typeof item?.section_number === 'string' ? item.section_number.trim() : '';
      if (name) return `name:${name}`;
      if (number) return `num:${number}`;
      return 'other';
    };

    const getGroupLabel = (item) => {
      const name = typeof item?.section_name === 'string' ? item.section_name.trim() : '';
      const number = typeof item?.section_number === 'string' ? item.section_number.trim() : '';
      if (name) return name;
      if (number) return `Section ${number}`;
      return 'Other activities';
    };

    list.forEach((item, originalIndex) => {
      const key = getGroupKey(item);
      const label = getGroupLabel(item);
      const existingIdx = indexByKey.get(key);
      if (existingIdx === undefined) {
        indexByKey.set(key, groups.length);
        groups.push({
          key,
          label,
          items: [{ item, originalIndex }],
        });
        return;
      }

      groups[existingIdx].items.push({ item, originalIndex });
    });

    return groups;
  }, [items]);

  const currentGroupKey = useMemo(() => {
    const safeIndex = typeof currentIndex === 'number' ? currentIndex : -1;
    for (const group of groupedItems) {
      if (group.items.some(({ originalIndex }) => originalIndex === safeIndex)) {
        console.log(`\ud83c\udfaf [Accordion] Found group for index ${safeIndex}: ${group.key} (${group.label})`);
        return group.key;
      }
    }
    console.log(`⚠️ [Accordion] No group found for index ${safeIndex}`);
    return null;
  }, [groupedItems, currentIndex]);

  const groupKeySignature = useMemo(
    () => groupedItems.map((g) => g.key).join('|'),
    [groupedItems]
  );

  // Default: only open the current active section when groups change (e.g. new chapter/items).
  useEffect(() => {
    if (!groupedItems.length) return;
    if (prevGroupKeySignatureRef.current === groupKeySignature) return;
    prevGroupKeySignatureRef.current = groupKeySignature;

    // Skip if we have merged sections from scroll - they take priority
    if (multipleSectionKeys && Array.isArray(multipleSectionKeys) && multipleSectionKeys.length > 0) {
      console.log(`⏭️ [Accordion] Skipping groupedItems update - merged sections active:`, multipleSectionKeys);
      return;
    }

    // Only open the current active section
    if (currentGroupKey) {
      setOpenSections([currentGroupKey]);
    } else if (groupedItems.length > 0) {
      // Fallback: open first section if no active section
      setOpenSections([groupedItems[0].key]);
    }
  }, [groupedItems, groupKeySignature, currentGroupKey, multipleSectionKeys]); // Added multipleSectionKeys dependency

  // Switch to the currently active section when selection changes.
  // BUT: Don't override if we have scroll-based sections to display
  useEffect(() => {
    if (!currentGroupKey) return;

    // Skip if we have ANY scroll sections (single OR merged) - they take priority
    if (multipleSectionKeys && Array.isArray(multipleSectionKeys) && multipleSectionKeys.length > 0) {
      console.log(`⏭️ [Accordion] Skipping single-section update - scroll sections active:`, multipleSectionKeys);
      return;
    }

    console.log(`🔄 [Accordion] Switching to section: ${currentGroupKey}`);
    setOpenSections([currentGroupKey]);
  }, [currentGroupKey, multipleSectionKeys]);

  // Auto-scroll to active item when index changes or section opens
  useEffect(() => {
    if (typeof currentIndex !== 'number' || !items || !items[currentIndex]) return;

    // Use a small timeout to allow the accordion animation/state update to process first
    const timer = setTimeout(() => {
      const activeItem = items[currentIndex];
      if (!activeItem) return;

      const elementId = `activity-item-${activeItem.id}`;
      const el = document.getElementById(elementId);

      if (el) {
        console.log(`📜 [ActivityProgressList] Scrolling to item ${currentIndex}: ${activeItem.title}`);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        console.log(`⚠️ [ActivityProgressList] Element not found for scrolling: #${elementId}`);
      }
    }, 400); // 400ms to allow Accordion expansion animation to start/finish

    return () => clearTimeout(timer);
  }, [currentIndex, items, openSections]); // Re-run when sections open

  return (
    <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-xl font-bold text-foreground">
            Learning Pack Progress
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={(value) => setOpenSections(value)}
          className="w-full"
        >
          {groupedItems.map((group) => {
            const total = group.items.length;
            const completedCount = group.items.reduce((acc, { item }) => (completedItems?.[item.id] ? acc + 1 : acc), 0);

            return (
              <AccordionItem
                key={group.key}
                value={group.key}
                className="border-[hsl(var(--card-border))]"
              >
                <AccordionTrigger
                  className="py-3 px-3 rounded-md text-left hover:no-underline relative overflow-hidden group"
                >
                  {/* Full-width Progress Bar Background (Bottom Line) */}
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-secondary/30" />

                  {/* Full-width Active Progress Bar */}
                  <div
                    className="absolute bottom-0 left-0 h-[2px] bg-[hsl(var(--primary))] transition-all duration-500 ease-out"
                    style={{ width: `${(completedCount / total) * 100}%` }}
                  />

                  <div className="flex items-center justify-between gap-3 w-full relative z-10">
                    <div className="text-xs font-extrabold tracking-wide text-foreground truncate pr-4">
                      {group.label}
                    </div>
                    <div className="flex-shrink-0 font-mono text-[10px] text-muted-foreground font-medium">
                      {completedCount}/{total}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-2 pt-2">
                  <div className="space-y-3">
                    {group.items.map(({ item, originalIndex }) => {
                      const Icon = iconMap[item.type];
                      const isCompleted = completedItems[item.id];
                      const isCurrent = originalIndex === currentIndex;
                      const isUpcoming = originalIndex > currentIndex;
                      const gradient = typeColors[item.type];

                      // Backend stores this activity as `Watch` (AI-generated Learn & Discover).
                      // UI should display it as the learning experience name.
                      const displayType = item.type === 'Watch' ? 'Learn and Discover' : item.type;

                      const isClickable = typeof onSelectIndex === 'function';

                      return (
                        <button
                          key={item.id}
                          id={`activity-item-${item.id}`}
                          type="button"
                          onClick={isClickable ? () => onSelectIndex(originalIndex) : undefined}
                          className={`
                w-full text-left flex items-start gap-3 p-3 rounded-lg transition-all duration-300
                ${isCurrent ? 'bg-[hsl(var(--primary))]/20 border-2 border-[hsl(var(--primary))]' : 'bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))]'}
                ${isCompleted ? 'opacity-70' : ''}
                ${isClickable ? 'cursor-pointer hover:bg-[hsl(var(--sidebar-hover))]' : 'cursor-default'}
              `}
                        >
                          {/* Status Indicator */}
                          <div className="flex-shrink-0 mt-1">
                            {isCompleted ? (
                              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            ) : isCurrent ? (
                              <div className="w-6 h-6 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center animate-pulse">
                                <ArrowRight className="w-4 h-4 text-white" />
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center">
                                <Circle className="w-3 h-3 text-gray-400" />
                              </div>
                            )}
                          </div>

                          {/* Icon */}
                          <div className={`
                flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br ${gradient} 
                flex items-center justify-center shadow-md
                ${isUpcoming ? 'opacity-50' : ''}
              `}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div
                              className={
                                `text-[10px] font-extrabold tracking-wider uppercase mb-0.5 ` +
                                (isCurrent
                                  ? 'text-[hsl(var(--teal-glow))]'
                                  : isCompleted
                                    ? 'text-gray-500'
                                    : 'text-gray-400')
                              }
                            >
                              {String(displayType || '').toUpperCase()}
                            </div>
                            {typeof item?.one_liner === 'string' && item.one_liner.trim() ? (
                              <p
                                className={
                                  `text-xs mb-1 ` +
                                  (isCurrent
                                    ? 'text-gray-200'
                                    : isCompleted
                                      ? 'text-gray-500'
                                      : 'text-gray-400')
                                }
                              >
                                {item.one_liner.trim()}
                              </p>
                            ) : null}
                            <p className={`
                  text-xs
                  ${isCurrent ? 'text-gray-200' : 'text-gray-500'}
                `}>
                              {item.duration}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}
